import { Coupon } from '@dtx-company/true-common/src/types/Coupon'
import {
  Plan,
  PlanExtraDetail,
  PlanExtraName,
  PlanExtraRender,
  PlanPurchaseExperienceMode
} from '@dtx-company/true-common/src/types/Plan'
import {
  PlanCostSummary,
  PlanCostSummaryType
} from '@dtx-company/inter-app/src/types/PlanCostSummary'
import { PlanExtrasForm } from '../../../ProFlex/AddOns'
import { PlanPurchaseExperienceSummaryLine } from './PlanPurchaseExperienceSummaryLine'
import { PlanPurchaseExperienceSummaryLineCoupon } from './PlanPurchaseExperienceSummaryLineCoupon'
import { promoAppliesToSelectedPlan } from '../../utils/promoAppliesToSelectedPlan'
import { Box, Divider, Stack, Typography } from '@dtx-company/design-system/src'

export interface SummaryTopSectionProps {
  selectedPlan: Plan
  mode: PlanPurchaseExperienceMode | undefined
  promo: Partial<Coupon>
  planCostSummary: PlanCostSummary | undefined
  currentPlanExtraDetails?: PlanExtraDetail[]
  planExtraFormValues: PlanExtrasForm
  planExtraRender?: PlanExtraRender[]
}

export function PPESummaryTopSection(props: SummaryTopSectionProps): JSX.Element | null {
  const summary = props.planCostSummary

  if (props.mode === PlanPurchaseExperienceMode.CHANGE_PAYMENT) return null

  const requireChanges =
    props.planCostSummary?.type === PlanCostSummaryType.SAME &&
    props.mode === PlanPurchaseExperienceMode.PURCHASE_ADDONS
  if (requireChanges) {
    return (
      <>
        <Typography component="h2" variant="subtitleSmall" fontWeight="bold">
          Order Summary
        </Typography>
        <Stack sx={{ mt: 4, flexDirection: 'row', flexWrap: 'nowrap' }}>
          <Typography component="p" variant="bodySmall">
            Please make changes to your add-ons to see your new summary reflected here.
          </Typography>
        </Stack>
      </>
    )
  }

  return (
    <>
      <Typography component="h2" variant="subtitleSmall" fontWeight="bold">
        Order Summary
      </Typography>
      <Stack sx={{ mt: 4 }}>
        <PlanPurchaseExperienceSummaryLine
          appearance="primary"
          label="Base Price"
          priceInCents={summary?.basePriceInCents || 0}
        />
        {summary?.basePlanExtras?.map(extra => {
          return (
            <PlanPurchaseExperienceSummaryLine
              key={extra.planExtraId}
              appearance="primary"
              label={`${extra.quantity} ${PlanExtraName[extra.planExtraType]} included`}
              priceInCents={extra.priceInCents || 'FREE'}
            />
          )
        })}
        {summary?.additionalPlanExtras?.map(extra => {
          return (
            <PlanPurchaseExperienceSummaryLine
              key={extra.planExtraId}
              appearance="primary"
              label={`${extra.quantity} additional ${PlanExtraName[extra.planExtraType]}`}
              priceInCents={extra.priceInCents || 'FREE'}
            />
          )
        })}
        {(summary?.annualDiscountInCents ||
          summary?.prorationAppliedInCents ||
          summary?.couponDiscountInCents) && (
          <>
            <Box sx={{ mt: 4 }}>
              <PlanPurchaseExperienceSummaryLine
                appearance="primary"
                label="Adjustments"
                priceInCents={''}
              />
            </Box>
            <Box sx={{ pl: 4 }}>
              <PlanPurchaseExperienceSummaryLine
                appearance="secondary"
                label="Annual Discount"
                priceInCents={summary?.annualDiscountInCents || 0}
              />
              <PlanPurchaseExperienceSummaryLine
                appearance="secondary"
                label="Price adjustment from old plan to new"
                priceInCents={summary?.prorationAppliedInCents || 0}
              />
              <PlanPurchaseExperienceSummaryLineCoupon
                appearance="secondary"
                promo={
                  promoAppliesToSelectedPlan(props.promo, props.selectedPlan)
                    ? props.promo
                    : undefined
                }
                priceInCents={summary?.couponDiscountInCents}
              />
            </Box>
          </>
        )}
      </Stack>
      <Divider sx={{ mt: 6, mb: 6 }} />
      <Stack>
        <PlanPurchaseExperienceSummaryLine
          appearance="primary"
          label="Subtotal"
          priceInCents={summary?.subtotalInCents || 0}
        />
        {/* 
          Commenting this out because won't show the tax in the summary section anymore
          Not removing it because we might wanna display it in a another step later 
          See. https://app.shortcut.com/flowcode/story/84517

          <PlanPurchaseExperienceSummaryLineTax
            appearance="secondary"
            priceInCents={summary?.taxInCents}
            sx={{ mt: '16px' }}
          /> */}
        <Box sx={{ mt: '16px' }}>
          <PlanPurchaseExperienceSummaryLine
            appearance="secondary"
            label="Credits applied"
            priceInCents={summary?.creditAppliedInCents || 0}
          />
        </Box>
      </Stack>
    </>
  )
}
